/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { apiClient } from '@/lib/apiClient';

import type {
  AccessRequest,
  AccessRequestSubmission,
  CurrentUser,
  WorkspaceDefinition,
} from './access.models';

/**
 * Backend mount point. The only endpoints reachable by a caller who has authenticated but has no
 * access at all — everything else answers 403 until they are provisioned.
 */
const BASE = '/ediagnostics/v1/access';

/** Who the caller is and what they may reach. The first call the app makes after sign-in. */
export async function getCurrentUser(): Promise<CurrentUser> {
  const { data } = await apiClient.get<CurrentUser>(`${BASE}/me`);
  return data;
}

/** Workspace catalogue: display names, descriptions and which ones may be self-requested. */
export async function getWorkspaces(): Promise<WorkspaceDefinition[]> {
  const { data } = await apiClient.get<WorkspaceDefinition[]>(`${BASE}/workspaces`);
  return data;
}

/** Asks for access. 409 if a request is already waiting on an admin. */
export async function submitAccessRequest(
  submission: AccessRequestSubmission,
): Promise<AccessRequest> {
  const { data } = await apiClient.post<AccessRequest>(`${BASE}/requests`, submission);
  return data;
}

/**
 * The caller's most recent request, or null when they have never asked — a 204, which axios
 * surfaces as an empty body. That distinction is what chooses between the form and the
 * "waiting on an admin" screen.
 */
export async function getMyAccessRequest(): Promise<AccessRequest | null> {
  const { data } = await apiClient.get<AccessRequest | ''>(`${BASE}/requests/mine`);
  return data || null;
}
